import type { CategoryScore, ScoringResult } from './engine';

/**
 * A scoring result read as points, for forms under the points model.
 *
 * <p>The engine speaks in ratios out of 100, and under the points model the customer always
 * sets the target to 100, so the audit's total already reads as points. A category does not:
 * two of two questions answered yes in a twenty-five point category is 100 as a ratio and 25
 * as points, and the auditor who typed 15 and 10 read the 100 as a fault. So did the running
 * figure, which is a ratio over what has been answered so far, not what has been earned.</p>
 *
 * <p>Worth is what the customer's workbook calls "Hesaplama" (ADR-022): a question ruled out
 * hands its points to the rest of its category, and a category with nothing left hands its
 * points to the other categories, so the figures on screen always add up to the form's
 * total. Nothing here changes a score. It is a reading of the same result, and the number of
 * record, the bands and every average stay on the engine's ratio.</p>
 */

/**
 * Whether every scored question in the category is out of this visit — ruled out, exempt or
 * never shown. Such a category is not applicable: the engine leaves it out of the total and
 * the others carry its share. One nobody has opened yet is not this; it still counts.
 */
export function isRuledOut(category: CategoryScore): boolean {
  const scored = category.questions.filter((q) => q.exclusion !== 'NotScored');
  return scored.length > 0 && scored.every((q) => q.exclusion !== 'None');
}

/**
 * How much the categories still in play grow so that, between them, they are worth the
 * form's declared total again. One when nothing is ruled out.
 */
function growth(result: ScoringResult): number {
  const declared = result.categories.reduce((sum, c) => sum + c.declaredWeight, 0);
  const live = result.categories
    .filter((c) => !isRuledOut(c))
    .reduce((sum, c) => sum + c.weightInForm, 0);

  return live > 0 ? declared / live : 0;
}

/**
 * What the category is worth on this visit: 62,5 for a fifty-point category when a
 * twenty-point one beside it was ruled out entirely. Null when it is itself ruled out.
 */
export function categoryWorth(result: ScoringResult, category: CategoryScore): number | null {
  if (isRuledOut(category)) return null;
  return category.weightInForm * growth(result);
}

/**
 * What one question is worth on this visit: its share of the questions still in play in its
 * category, times what the category is worth. 25 for each of two tens in a fifty-point
 * category whose thirty was ruled out. Null for a question that is itself out.
 */
export function questionWorth(
  result: ScoringResult,
  category: CategoryScore,
  questionId: string,
): number | null {
  const worth = categoryWorth(result, category);
  const question = category.questions.find((q) => q.questionId === questionId);
  if (worth === null || !question || question.exclusion !== 'None') return null;

  const live = category.questions
    .filter((q) => q.exclusion === 'None')
    .reduce((sum, q) => sum + q.weight, 0);

  return live > 0 ? (question.weight * worth) / live : null;
}

/**
 * What the category has earned on this visit, in the points it is worth today.
 *
 * <p>Earned over what can still be earned, times what the category is worth: a question ruled
 * out inside the category hands its points to the others, as the engine's ratio does, and an
 * unanswered one simply has not earned anything yet. Complete, this is the ratio times the
 * category's worth; zero when a critical question failed, like the ratio.</p>
 */
export function categoryPoints(result: ScoringResult, category: CategoryScore): number | null {
  if (category.score === null) return null;
  if (category.zeroedByCritical) return 0;

  let earned = 0;
  let applicable = 0;

  for (const q of category.questions) {
    if (!q.counted || q.exclusion !== 'None') continue;

    applicable += q.weight;
    if (q.ratio !== null) earned += q.weight * q.ratio;
  }

  return applicable > 0 ? (earned * (categoryWorth(result, category) ?? 0)) / applicable : 0;
}

/**
 * What the whole audit has earned so far, out of the form's declared total.
 *
 * <p>The categories' own points, which already add up to the declared total between them:
 * finished, this lands on the engine's score, which is the figure the auditor ends on and the
 * one recorded.</p>
 */
export function runningPoints(result: ScoringResult): number | null {
  if (!result.isScored || result.runningScore === null) return null;
  // The engine sets the running figure to zero when a form-scope critical question fails,
  // before the audit is complete and so before zeroedByCritical is set.
  if (result.zeroedByCritical || result.runningScore === 0) return 0;
  if (growth(result) <= 0) return null;

  return result.categories.reduce((sum, c) => sum + (categoryPoints(result, c) ?? 0), 0);
}
